/**
 * Super Admin — Manage Certification Body Accreditations
 * Full governance table with inline actions, create modal, evidence upload.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';

// ── Status badge helpers ───────────────────────────────────────────────────────

const STATUS_BADGE = {
  pending:   { bg: '#e0e0e0', color: '#555',    label: 'Pending'   },
  approved:  { bg: '#d4edda', color: '#155724', label: 'Approved'  },
  suspended: { bg: '#fff3cd', color: '#856404', label: 'Suspended' },
  revoked:   { bg: '#f8d7da', color: '#721c24', label: 'Revoked'   },
  expired:   { bg: '#d6d8db', color: '#383d41', label: 'Expired'   },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { bg: '#eee', color: '#333', label: status };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: s.bg,
      color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ── Overlay modal ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'white', borderRadius: '8px', padding: '30px',
        maxWidth: '520px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1a1a1a' }}>{title}</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666', lineHeight: 1,
          }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Shared input style ────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '10px', border: '1px solid #d4af37',
  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
};

const btnPrimary = {
  padding: '10px 20px', background: '#d4af37', color: '#1a1a1a',
  border: 'none', borderRadius: '6px', fontSize: '14px',
  fontWeight: '600', cursor: 'pointer',
};

const btnSecondary = {
  padding: '10px 20px', background: '#6c757d', color: 'white',
  border: 'none', borderRadius: '6px', fontSize: '14px',
  fontWeight: '600', cursor: 'pointer',
};

const btnDanger = {
  padding: '10px 20px', background: '#dc3545', color: 'white',
  border: 'none', borderRadius: '6px', fontSize: '14px',
  fontWeight: '600', cursor: 'pointer',
};

// ── Status filter pills ───────────────────────────────────────────────────────

const STATUS_FILTERS = ['all', 'pending', 'approved', 'suspended', 'revoked', 'expired'];

function FilterPills({ active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
      {STATUS_FILTERS.map(f => (
        <button
          key={f}
          onClick={() => onChange(f)}
          style={{
            padding: '6px 16px',
            borderRadius: '20px',
            border: active === f ? '2px solid #d4af37' : '2px solid #ddd',
            background: active === f ? '#d4af37' : 'white',
            color: active === f ? '#1a1a1a' : '#555',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            textTransform: 'capitalize',
          }}
        >
          {f === 'all' ? 'All' : STATUS_BADGE[f]?.label || f}
        </button>
      ))}
    </div>
  );
}

// ── Per-row action buttons ────────────────────────────────────────────────────

function RowActions({ acc, onAction, onEvidence }) {
  const status = acc.status;
  const tiny = { padding: '4px 10px', border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' };

  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {status === 'pending' && (
        <>
          <button onClick={() => onAction('approve', acc)} style={{ ...tiny, background: '#28a745', color: 'white' }}>Approve</button>
          <button onClick={() => onAction('revoke',  acc)} style={{ ...tiny, background: '#dc3545', color: 'white' }}>Revoke</button>
        </>
      )}
      {status === 'approved' && (
        <>
          <button onClick={() => onAction('suspend', acc)} style={{ ...tiny, background: '#ffc107', color: '#333' }}>Suspend</button>
          <button onClick={() => onAction('revoke',  acc)} style={{ ...tiny, background: '#dc3545', color: 'white' }}>Revoke</button>
        </>
      )}
      {status === 'suspended' && (
        <>
          <button onClick={() => onAction('reinstate', acc)} style={{ ...tiny, background: '#17a2b8', color: 'white' }}>Reinstate</button>
          <button onClick={() => onAction('revoke',    acc)} style={{ ...tiny, background: '#dc3545', color: 'white' }}>Revoke</button>
        </>
      )}
      {(status === 'revoked' || status === 'expired') && (
        <button onClick={() => onAction('reinstate', acc)} style={{ ...tiny, background: '#17a2b8', color: 'white' }}>Reinstate</button>
      )}
      <button onClick={() => onEvidence(acc)} style={{ ...tiny, background: '#6c757d', color: 'white' }}>Evidence</button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const ManageCBAccreditations = () => {
  const [accreditations, setAccreditations] = useState([]);
  const [certBodies, setCertBodies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ cert_body_id: '', notes: '' });
  const [createSaving, setCreateSaving] = useState(false);

  // Action confirmation modal
  const [actionModal, setActionModal] = useState(null); // { action, acc }
  const [actionNotes, setActionNotes] = useState('');
  const [actionSaving, setActionSaving] = useState(false);

  // Evidence upload modal
  const [evidenceModal, setEvidenceModal] = useState(null); // acc
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceSaving, setEvidenceSaving] = useState(false);

  const flash = (msg, isError = false) => {
    if (isError) setError(msg); else setMessage(msg);
    setTimeout(() => { setError(''); setMessage(''); }, 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { simple_status: statusFilter } : {};
      const [accRes, cbRes] = await Promise.all([
        axiosClient.get('/accreditations/', { params }),
        axiosClient.get('/cert-bodies/'),
      ]);
      setAccreditations(accRes.data);
      setCertBodies(cbRes.data);
    } catch (err) {
      flash('Failed to load accreditations: ' + (err.response?.data?.detail || err.message), true);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Create ──────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!createForm.cert_body_id) { flash('Please select a certification body', true); return; }
    setCreateSaving(true);
    try {
      await axiosClient.post('/accreditations/', {
        cert_body_id: parseInt(createForm.cert_body_id),
        notes: createForm.notes || null,
      });
      setShowCreate(false);
      setCreateForm({ cert_body_id: '', notes: '' });
      flash('Accreditation created successfully');
      fetchData();
    } catch (err) {
      flash('Failed to create: ' + (err.response?.data?.detail || err.message), true);
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Action (approve / suspend / revoke / reinstate) ─────────────────────────

  const handleActionConfirm = async () => {
    if (!actionModal) return;
    setActionSaving(true);
    try {
      await axiosClient.patch(`/accreditations/${actionModal.acc.id}/${actionModal.action}`, {
        notes: actionNotes || null,
      });
      setActionModal(null);
      setActionNotes('');
      flash(`Accreditation ${actionModal.action}d successfully`);
      fetchData();
    } catch (err) {
      flash('Action failed: ' + (err.response?.data?.detail || err.message), true);
    } finally {
      setActionSaving(false);
    }
  };

  // ── Evidence upload ─────────────────────────────────────────────────────────

  const handleEvidenceUpload = async () => {
    if (!evidenceFile || !evidenceModal) return;
    setEvidenceSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', evidenceFile);
      await axiosClient.post(`/accreditations/${evidenceModal.id}/upload-evidence`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEvidenceModal(null);
      setEvidenceFile(null);
      flash('Evidence uploaded successfully');
      fetchData();
    } catch (err) {
      flash('Upload failed: ' + (err.response?.data?.detail || err.message), true);
    } finally {
      setEvidenceSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const ACTION_LABELS = {
    approve: 'Approve',
    suspend: 'Suspend',
    revoke: 'Revoke',
    reinstate: 'Reinstate',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a1a1a' }}>
            CB Accreditations
          </h1>
          <p style={{ margin: '6px 0 0', color: '#666', fontSize: '14px' }}>
            Manage ICSC accreditations for all certification bodies
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnPrimary}>
          + New Accreditation
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ padding: '12px 16px', background: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '16px' }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ padding: '12px 16px', background: '#d4edda', color: '#155724', borderRadius: '6px', marginBottom: '16px' }}>
          {message}
        </div>
      )}

      {/* Filter pills */}
      <FilterPills active={statusFilter} onChange={setStatusFilter} />

      {/* Count */}
      <p style={{ color: '#666', marginBottom: '12px', fontSize: '13px' }}>
        {loading ? 'Loading…' : `${accreditations.length} accreditation(s)`}
      </p>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div className="ds-table-scroll">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1a1a1a' }}>
              {['ID', 'Certification Body', 'Accreditation #', 'Status', 'Issued At', 'Expires At', 'Evidence', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: 'white', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && accreditations.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  No accreditations found
                </td>
              </tr>
            )}
            {accreditations.map((acc, idx) => (
              <tr key={acc.id} style={{ borderBottom: '1px solid #f0f0f0', background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '12px 14px', fontSize: '13px', color: '#666' }}>{acc.id}</td>
                <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: '500' }}>{acc.cert_body_name || '—'}</td>
                <td style={{ padding: '12px 14px', fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>
                  {acc.accreditation_number}
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <StatusBadge status={acc.status} />
                </td>
                <td style={{ padding: '12px 14px', fontSize: '13px', color: '#555' }}>
                  {acc.issued_at ? new Date(acc.issued_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '12px 14px', fontSize: '13px', color: '#555' }}>
                  {acc.expires_at ? new Date(acc.expires_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '12px 14px', fontSize: '13px' }}>
                  {acc.evidence_url ? (
                    <a href={acc.evidence_url} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#d4af37', textDecoration: 'none', fontWeight: '600' }}>
                      View
                    </a>
                  ) : (
                    <span style={{ color: '#ccc' }}>None</span>
                  )}
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <RowActions
                    acc={acc}
                    onAction={(action, a) => { setActionModal({ action, acc: a }); setActionNotes(''); }}
                    onEvidence={(a) => { setEvidenceModal(a); setEvidenceFile(null); }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* ── Create Modal ──────────────────────────────────────────────────────── */}
      {showCreate && (
        <Modal title="New Accreditation" onClose={() => setShowCreate(false)}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Certification Body *
            </label>
            <select
              value={createForm.cert_body_id}
              onChange={e => setCreateForm(f => ({ ...f, cert_body_id: e.target.value }))}
              style={inputStyle}
            >
              <option value="">— Select a certification body —</option>
              {certBodies.map(cb => (
                <option key={cb.id} value={cb.id}>{cb.name || cb.company_name}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Notes (optional)
            </label>
            <textarea
              value={createForm.notes}
              onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Initial application notes…"
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowCreate(false)} style={btnSecondary}>Cancel</button>
            <button onClick={handleCreate} disabled={createSaving} style={btnPrimary}>
              {createSaving ? 'Creating…' : 'Create Accreditation'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Action Confirmation Modal ─────────────────────────────────────────── */}
      {actionModal && (
        <Modal
          title={`${ACTION_LABELS[actionModal.action]} Accreditation`}
          onClose={() => setActionModal(null)}
        >
          <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
            You are about to <strong>{actionModal.action}</strong> the accreditation for{' '}
            <strong>{actionModal.acc.cert_body_name}</strong> ({actionModal.acc.accreditation_number}).
          </p>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Notes / Reason (optional)
            </label>
            <textarea
              value={actionNotes}
              onChange={e => setActionNotes(e.target.value)}
              rows={3}
              placeholder="Enter reason for this decision…"
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setActionModal(null)} style={btnSecondary}>Cancel</button>
            <button
              onClick={handleActionConfirm}
              disabled={actionSaving}
              style={actionModal.action === 'revoke' ? btnDanger : btnPrimary}
            >
              {actionSaving ? 'Saving…' : ACTION_LABELS[actionModal.action]}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Evidence Upload Modal ─────────────────────────────────────────────── */}
      {evidenceModal && (
        <Modal title="Upload Evidence" onClose={() => setEvidenceModal(null)}>
          <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
            Upload evidence document for{' '}
            <strong>{evidenceModal.cert_body_name}</strong> ({evidenceModal.accreditation_number}).
          </p>
          {evidenceModal.evidence_url && (
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
              Current:{' '}
              <a href={evidenceModal.evidence_url} target="_blank" rel="noopener noreferrer"
                style={{ color: '#d4af37' }}>
                View existing evidence
              </a>
            </p>
          )}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
              Select File *
            </label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={e => setEvidenceFile(e.target.files[0] || null)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            />
            <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
              Max 10 MB · PDF, PNG, JPG, DOC, DOCX
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setEvidenceModal(null)} style={btnSecondary}>Cancel</button>
            <button
              onClick={handleEvidenceUpload}
              disabled={!evidenceFile || evidenceSaving}
              style={{ ...btnPrimary, opacity: (!evidenceFile || evidenceSaving) ? 0.6 : 1 }}
            >
              {evidenceSaving ? 'Uploading…' : 'Upload Evidence'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ManageCBAccreditations;
